import { genXAPIValidData } from '@vedalib/courseAnalitycs'
import { useCallback, useMemo } from 'react'

import { sendCourseAnalytics } from 'services/Store/Project/actions'
import { PreviewMode } from 'services/Store/Project/enums'
import { useProjectContext, useProjectDispatch } from 'services/Store/Project/hooks'
import { getProgress, getProject, getSection } from 'services/Store/Project/selectors'
import type { IBlockMode } from 'services/Store/Project/types'

import { LmsService } from '../lms.service'
import { useEntityViewTracking } from './useEntityViewTracking'

interface IUseSectionViewed {
  mode: IBlockMode
}

export const useSectionViewed = ({ mode }: IUseSectionViewed) => {
  const project = useProjectContext(getProject)
  const dispatch = useProjectDispatch()
  const section = useProjectContext(getSection)

  const needViewed = mode.previewMode === PreviewMode.scorm
  const progress = useProjectContext(getProgress) ?? 0
  const { sectionId, projectId, companyId } = useMemo(
    () => ({
      sectionId: section?.id || '',
      projectId: project?.id || '',
      companyId: project?.companyId || '',
    }),
    [section, project],
  )

  const { sectionExitedData, sectionEnteredData } = useMemo(() => {
    const sectionExitedData = genXAPIValidData({
      elementType: 'exitedSection',
      verb: 'exitedSection',
      data: {
        elementId: sectionId,
        resultExtensions: { sectionType: section?.type, sectionId, courseId: projectId },
        objectExtensions: { projectId, courseId: projectId, sectionId, companyId, progress },
      },
    }).xAPIData

    const sectionEnteredData = genXAPIValidData({
      elementType: 'enteredSection',
      verb: 'enteredSection',
      data: {
        elementId: sectionId,
        resultExtensions: { sectionType: section?.type, sectionId, courseId: projectId },
        objectExtensions: { projectId, courseId: projectId, sectionId, companyId, progress },
      },
    }).xAPIData
    return { sectionExitedData, sectionEnteredData }
  }, [sectionId, projectId, companyId, progress, section])

  const setViewed = useCallback(
    () => dispatch(sendCourseAnalytics(sectionEnteredData)),
    [dispatch, sectionEnteredData],
  )

  const setUnViewed = useCallback(
    () => dispatch(sendCourseAnalytics(sectionExitedData)),
    [dispatch, sectionExitedData],
  )

  const setUnViewedWaiting = useCallback(
    () => LmsService.instance.sendAnalyticsViaBeacon([sectionExitedData]),
    [sectionExitedData],
  )

  useEntityViewTracking({
    setUnViewed,
    setViewed,
    setUnViewedWaiting,
    needViewed,
    exitObserve: true,
  })
}
